import type {
  ApiKey, CreatedApiKey, JobDetail, ListJobsResult, ScraperSchema, User,
} from "./types";

export class ApiError extends Error {
  constructor(public status: number, public body: unknown, message: string) {
    super(message);
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  // Only attach Content-Type when there's a body; Fastify rejects empty JSON
  // bodies with FST_ERR_CTP_EMPTY_JSON_BODY on method+no-body requests.
  const baseHeaders: Record<string, string> = { Accept: "application/json" };
  if (init?.body != null) baseHeaders["Content-Type"] = "application/json";
  const res = await fetch(path, {
    credentials: "include",
    headers: { ...baseHeaders, ...(init?.headers ?? {}) },
    ...init,
  });
  const text = await res.text();
  const parsed = text ? (() => { try { return JSON.parse(text); } catch { return text; } })() : null;
  if (!res.ok) {
    const msg = typeof parsed === "object" && parsed && "error" in parsed
      ? String((parsed as { error: string }).error)
      : `HTTP ${res.status}`;
    throw new ApiError(res.status, parsed, msg);
  }
  return parsed as T;
}

export const api = {
  me: () => call<{ user: User }>("/me"),
  login: (email: string, password: string) =>
    call<{ user: User }>("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),
  logout: () => call<{ ok: true }>("/auth/logout", { method: "POST" }),

  scrapers: () => call<{ scrapers: ScraperSchema[] }>("/ui/config"),
  listTasks: (params: { page?: number; per_page?: number; scraper?: string; status?: string }) => {
    const qs = new URLSearchParams();
    if (params.page) qs.set("page", String(params.page));
    if (params.per_page) qs.set("per_page", String(params.per_page));
    if (params.scraper) qs.set("scraper", params.scraper);
    if (params.status) qs.set("status", params.status);
    return call<ListJobsResult>(`/ui/tasks?${qs.toString()}`);
  },
  getTask: (id: string) => call<JobDetail>(`/jobs/${id}`),
  getTaskMeta: (id: string) => call<JobDetail>(`/jobs/${id}?meta=1`),
  runScraper: (name: string, input: unknown, bypassCache = false) =>
    call<{ id: string; status: string }>(`/scrape/${name}`, {
      method: "POST",
      body: JSON.stringify({ input, bypass_cache: bypassCache }),
    }),
  cancelTask: (id: string) => call<{ ok: true }>(`/jobs/${id}/cancel`, { method: "POST" }),
  deleteTask: (id: string) => call<{ ok: true }>(`/jobs/${id}`, { method: "DELETE" }),

  listApiKeys: () => call<{ keys: ApiKey[] }>("/api-keys"),
  createApiKey: (label: string) =>
    call<CreatedApiKey>("/api-keys", { method: "POST", body: JSON.stringify({ label }) }),
  revokeApiKey: (id: string) => call<{ ok: true }>(`/api-keys/${id}`, { method: "DELETE" }),
};
